import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { firstOfNextMonthYmd, parseDateLocal } from "@/lib/utils";
import { resolveEnrollmentEffectiveDate } from "@shared/lib/ageDetermination";
import { PLAN_TYPE_OPTIONS } from "../../../../../shared/schemas/plans";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Database, Pencil } from "lucide-react";
import { SystemPlanCombobox, type SystemPlan } from "./SystemPlanCombobox";
import { PlanBuilderForm } from "@/components/plans/PlanBuilderForm";

/**
 * Add a plan to a client, a prospect, or the agency itself (the plans the
 * agency offers its own employees — agency_plans). Every plan lives in the
 * Plan Library (plans.clientId IS NULL); the plan↔employer link is the
 * many-to-many client_plans junction, never a client-scoped clone. Two paths:
 *
 * 1. From Library — link an existing Administr library plan (curated/vendor),
 *    or a custom plan already built for this client. No copy: rates read
 *    through the link from the library plan's versioned table, locked per
 *    plan year
 *    (docs/PLAN_RATING_ACCEPTANCE_SPEC.md — no-copied-rates invariant, sc. 08).
 * 2. Create New — build a plan MANUALLY. It's created as a library plan owned
 *    by the broker's agency (agency-private) and auto-linked to this
 *    client/prospect/agency, then opened for rate/benefit configuration.
 *    Vendor import (W&B etc.) is superadmin-only, done in the SuperAdmin Plan
 *    Library.
 */
interface AddPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: string;
  prospectId?: string;
  /** Agency Benefits tab: the plan is added to the agency's own offering. */
  agencyId?: string;
}

interface Carrier {
  id: number;
  name: string;
}

const firstOfNextMonth = (): string => firstOfNextMonthYmd();

function clientPlanLinkBody(
  clientId: number,
  planId: number,
  effectiveDate: string,
  ratingArea: { areaId: string; state: string } | null,
) {
  const eff = parseDateLocal(effectiveDate) ?? new Date();
  const endDate = new Date(eff.getFullYear() + 1, eff.getMonth(), eff.getDate() - 1);
  const renewalDate = new Date(eff.getFullYear() + 1, eff.getMonth(), eff.getDate());
  return {
    clientId,
    planId,
    effectiveDate: eff,
    endDate,
    renewalDate,
    renewalMonth: renewalDate.getMonth() + 1,
    status: "active",
    isActive: true,
    enrolledEmployees: 0,
    totalPremium: "0.00",
    // Bug: the "Rating Area" step (below) only filtered which plans showed in
    // the picker - it was never sent to the server, so the link always fell
    // back to the client's own stored address for rating, silently
    // overriding whatever area the broker picked here. Server resolves this
    // pair back to a real zip/county for THIS link only (clientPlanRoutes.ts)
    // - it never touches the client's own address/rating area.
    ratingAreaOverride: ratingArea?.areaId ?? null,
    ratingAreaState: ratingArea?.state ?? null,
  };
}

export function AddPlanDialog({
  open,
  onOpenChange,
  clientId,
  prospectId,
  agencyId,
}: AddPlanDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const parsedClientId = clientId ? parseInt(clientId, 10) : NaN;
  const parsedProspectId = prospectId ? parseInt(prospectId, 10) : NaN;
  const hasClient = Number.isFinite(parsedClientId) && parsedClientId > 0;
  const hasProspect = Number.isFinite(parsedProspectId) && parsedProspectId > 0;
  const hasAgency = !hasClient && !hasProspect && !!agencyId;

  const [tab, setTab] = useState<"library" | "manual">("library");

  // --- From Library ---
  const [libraryPlan, setLibraryPlan] = useState<SystemPlan | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<string>(firstOfNextMonth);
  // Enrollments are validated against the LIBRARY plan's own year (client and
  // server both use resolveEnrollmentEffectiveDate) — linking a version whose
  // year doesn't contain the chosen effective date produces a client plan no
  // employee can be enrolled into on that date. Block it here, where the
  // broker can still pick the right plan-year version.
  const planYearMismatch = useMemo(() => {
    if (!hasClient || !libraryPlan || !effectiveDate) return null;
    const lp = libraryPlan as any;
    if (!lp.effectiveDate) return null; // dateless library plans: nothing to check
    const resolved = resolveEnrollmentEffectiveDate(effectiveDate, lp);
    if (!resolved.tooEarly && !resolved.tooLate) return null;
    const range = resolved.planEnd
      ? `${resolved.planStart} to ${resolved.planEnd}`
      : `${resolved.planStart} or later`;
    return `${libraryPlan.name}'s plan year covers ${range}, so employees can't be enrolled on ${effectiveDate}. Pick the library version for that plan year, or adjust the date.`;
  }, [hasClient, libraryPlan, effectiveDate]);
  // Guided flow filters. Plan type starts UNSET — the flow is step-ordered
  // (date → state → type → carrier → area → plan) and each choice narrows
  // the next, like the quoting flow but in one dialog.
  const [filterCarrierId, setFilterCarrierId] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("");
  const [filterTier, setFilterTier] = useState<string>("all");
  const [filterAreaId, setFilterAreaId] = useState<string>("all");
  const [filterNetwork, setFilterNetwork] = useState<string>("all");

  // The full library pool (unfiltered by area) — drives the dependent
  // narrowing: which plan types exist, which carriers offer the picked type,
  // which metal tiers exist for that combination. Same endpoint the plan
  // combobox uses, so the final picker always agrees with the counts.
  const { data: libraryPool = [] } = useQuery<any[]>({
    queryKey: ["/api/plans", { scope: "library", clientId: hasClient ? parsedClientId : null, ratingAreaId: null }],
    queryFn: async () => {
      const params = new URLSearchParams({ scope: "library" });
      if (hasClient) params.set("clientId", String(parsedClientId));
      const res = await fetch(`/api/plans?${params.toString()}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: open,
  });

  // Pool narrowed to plans whose own plan year contains the chosen effective
  // date (dateless plans pass) — versions for other years can't be enrolled
  // on that date, so they don't count toward the step options.
  const datedPool = useMemo(() => {
    if (!hasClient || !effectiveDate) return libraryPool;
    return libraryPool.filter((p: any) => {
      if (!p.effectiveDate) return true;
      const resolved = resolveEnrollmentEffectiveDate(effectiveDate, p);
      return !resolved.tooEarly && !resolved.tooLate;
    });
  }, [libraryPool, hasClient, effectiveDate]);

  const typeOptions = useMemo(() => {
    const types = new Set<string>();
    for (const p of datedPool) {
      const t = String(p.type || "").toLowerCase();
      if (t) types.add(t);
    }
    return PLAN_TYPE_OPTIONS.filter((opt: any) => types.has(opt.value));
  }, [datedPool]);

  const carrierIdsForType = useMemo(() => {
    if (!filterType) return null;
    const ids = new Set<number>();
    for (const p of datedPool) {
      if (String(p.type || "").toLowerCase() !== filterType) continue;
      if (p.carrierId != null) ids.add(p.carrierId);
    }
    return ids;
  }, [datedPool, filterType]);

  const tiersForSelection = useMemo(() => {
    const tiers = new Set<string>();
    for (const p of datedPool) {
      if (filterType && String(p.type || "").toLowerCase() !== filterType) continue;
      if (filterCarrierId !== "all" && String(p.carrierId) !== filterCarrierId) continue;
      if (p.metalTier) tiers.add(p.metalTier);
    }
    return ["Bronze", "Silver", "Gold", "Platinum", "Catastrophic"].filter((t) => tiers.has(t));
  }, [datedPool, filterType, filterCarrierId]);

  const isHealthType = filterType === "health" || filterType === "medical";
  // Changing plan type invalidates every downstream pick.
  useEffect(() => {
    setFilterCarrierId("all");
    setFilterTier("all");
    setFilterNetwork("all");
    setLibraryPlan(null);
  }, [filterType]);
  // Named networks in use, scoped to the picked carrier when one is set.
  const { data: networkOptions = [] } = useQuery<string[]>({
    queryKey: ["/api/plan-networks", filterCarrierId],
    queryFn: async () => {
      const params = filterCarrierId !== "all" ? `?carrierId=${filterCarrierId}` : "";
      const res = await fetch(`/api/plan-networks${params}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: open,
  });
  // Carrier change can invalidate the picked network — reset to "all".
  useEffect(() => {
    setFilterNetwork("all");
  }, [filterCarrierId]);

  // The client's rating area, resolved from its primary location — this is
  // the area the plan-year lock will use at charge time, so the picker
  // defaults its Rate Area filter to it (broker can widen back to All).
  const { data: clientInfo } = useQuery<any>({
    queryKey: ["/api/clients", clientId, "addplan"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: open && hasClient,
  });
  const { data: resolvedArea } = useQuery<any>({
    queryKey: ["/api/rating-areas/lookup", clientInfo?.zipCode ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({ zipCode: String(clientInfo.zipCode) });
      if (clientInfo.state) params.set("state", clientInfo.state);
      if (clientInfo.county) params.set("county", clientInfo.county);
      const res = await fetch(`/api/rating-areas/lookup?${params.toString()}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: Boolean(clientInfo?.zipCode),
  });
  // Clients without a saved location can't resolve a state — let the broker
  // type one so the Rate Area filter still works (Loom: payroll test client
  // had no zip and the picker came up empty).
  const [manualAreaState, setManualAreaState] = useState("");
  const areaState = (clientInfo?.state || manualAreaState).trim().toUpperCase();
  const { data: stateAreas = [] } = useQuery<any[]>({
    queryKey: ["/api/rating-areas", areaState || null],
    queryFn: async () => {
      const res = await fetch(`/api/rating-areas?state=${areaState}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: /^[A-Z]{2}$/.test(areaState),
  });
  // The rating-areas endpoint can return one row per source system for the
  // same area id (legacy label + geo label) — keep one per id.
  const uniqueStateAreas = useMemo(() => {
    const byId = new Map<string, any>();
    for (const a of stateAreas) if (!byId.has(String(a.id))) byId.set(String(a.id), a);
    return Array.from(byId.values());
  }, [stateAreas]);
  // Default the area filter to the client's resolved area, once. Use the area
  // id — the same key the SelectItems and the server-side rate filter use.
  const resolvedAreaNumber = resolvedArea?.id ?? resolvedArea?.areaNumber ?? null;
  useEffect(() => {
    if (resolvedAreaNumber != null && filterAreaId === "all") {
      setFilterAreaId(String(resolvedAreaNumber));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAreaNumber]);

  const { data: carriers = [] } = useQuery<Carrier[]>({
    queryKey: ["/api/carriers"],
    enabled: open,
  });

  const invalidateLinks = () => {
    if (hasClient) {
      queryClient.invalidateQueries({ queryKey: ["/api/client-plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/client-plans/count"] });
    }
    if (hasProspect) {
      queryClient.invalidateQueries({ queryKey: ["/api/prospects", prospectId, "plans"] });
    }
    if (hasAgency) {
      queryClient.invalidateQueries({ queryKey: ["/api/agency-plans"] });
    }
    queryClient.invalidateQueries({ queryKey: ["/api/plans"] });
  };

  const linkMutation = useMutation({
    mutationFn: async () => {
      if (!libraryPlan) throw new Error("Select a library plan first");
      if (hasClient) {
        const ratingArea =
          isHealthType && filterAreaId !== "all" && areaState ? { areaId: filterAreaId, state: areaState } : null;
        return apiRequest(
          "POST",
          "/api/client-plans",
          clientPlanLinkBody(parsedClientId, libraryPlan.id, effectiveDate, ratingArea),
        );
      }
      if (hasProspect) {
        return apiRequest("POST", `/api/prospects/${parsedProspectId}/plans`, { planId: libraryPlan.id });
      }
      if (hasAgency) {
        return apiRequest("POST", "/api/agency-plans", { planId: libraryPlan.id });
      }
      throw new Error("A client or prospect is required to add a plan");
    },
    onSuccess: () => {
      invalidateLinks();
      // A future plan-year effective date means the plan lands under the
      // "Future" plan-state filter — say so, or it looks like the add failed.
      const isFutureEffective = (() => {
        if (!effectiveDate) return false;
        const d = parseDateLocal(effectiveDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return !!d && d > today;
      })();
      toast({
        title: "Plan added",
        description: hasClient
          ? `${libraryPlan?.name ?? "Plan"} is now offered to this client.${
              isFutureEffective
                ? ` Its plan year starts ${effectiveDate}, so it's listed under Plan State: "Future".`
                : " Configure contributions and eligibility next."
            }`
          : hasAgency
            ? `${libraryPlan?.name ?? "Plan"} was added to your agency's plans.`
            : `${libraryPlan?.name ?? "Plan"} was added to this prospect.`,
        duration: isFutureEffective ? 9000 : undefined,
      });
      setLibraryPlan(null);
      setEffectiveDate(firstOfNextMonth());
      onOpenChange(false);
    },
    onError: (error: any) => {
      const message = String(error?.message || "");
      const lower = message.toLowerCase();
      toast({
        title: "Failed to add plan",
        description: lower.includes("retired")
          ? "This library plan is retired and cannot be linked. Select a replacement."
          : lower.includes("already_linked") || lower.includes("already added")
            ? `This plan is already added to the client. If you don't see it, check the Plan State filter on the Benefits tab (future-dated plans are under "Future").`
            : "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Create from the SHARED Plan Builder form (same UI the superadmin library
  // uses). Details post to /api/plans (library plan, agency-stamped); rates
  // publish through the same versioned endpoints the broker rate editor uses;
  // then the plan is auto-linked here.
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const effectiveDateStr: string = data.effectiveDate || firstOfNextMonth();
      // Create as a LIBRARY plan (clientId null). The server stamps the caller's
      // agencyId, so it's private to this agency's library.
      const created: any = await apiRequest("POST", "/api/plans", {
        name: String(data.name || "").trim(),
        type: data.type,
        carrierId: data.carrierId,
        planCode: String(data.planCode || "").trim(),
        networkType: data.networkType ?? undefined,
        network: data.network ?? undefined,
        metalTier: data.metalTier ?? undefined,
        description: data.description ?? undefined,
        rateStructureType: data.rateStructureType ?? undefined,
        ageDetermination: data.ageDetermination ?? undefined,
        availableStates: data.availableStates ?? undefined,
        rawBenefits: data.rawBenefits ?? undefined,
        clientId: null,
        source: "manual",
        effectiveDate: parseDateLocal(effectiveDateStr) ?? new Date(),
        commissionRate: "0",
        commissionCalculationType: "percentage",
        isActive: true,
        // Created from a client context → private to that client: other
        // brokers/clients won't see it in library pickers.
        visibleToClientId: hasClient ? parsedClientId : undefined,
      });
      const newPlan = typeof created?.json === "function" ? await created.json() : created;

      // Publish any rates entered in the builder through the versioned rate
      // writers (never raw inserts): tier rates in one call, age rates one
      // call per rating-area collection.
      const rates: any[] = Array.isArray(data.rates) ? data.rates : [];
      const tierRows = rates.filter((r) => r.rateStructureType === "tier_rates" && r.coverageTier);
      if (tierRows.length > 0) {
        await apiRequest("PUT", `/api/plan-tier-rates/bulk/${newPlan.id}`, {
          tierRates: Object.fromEntries(tierRows.map((r) => [r.coverageTier, r.monthlyRate])),
          // Per-area tier pricing (DEV-324): the tier-keyed map above keeps
          // only one area, so send every (area, tier) row too — the server
          // prefers these when present.
          tierRateRows: tierRows.some((r) => r.ratingAreaId != null)
            ? tierRows.map((r) => ({
                coverageTier: r.coverageTier,
                monthlyRate: r.monthlyRate,
                ratingAreaId: r.ratingAreaId ?? null,
                ratingAreaLabel: r.ratingAreaLabel ?? null,
              }))
            : undefined,
          effectiveStart: effectiveDateStr,
        });
      }
      const ageRows = rates.filter((r) => r.rateStructureType === "age_rates" && r.ageBand);
      const byArea = new Map<string, any[]>();
      for (const r of ageRows) {
        const key = r.ratingAreaId != null ? String(r.ratingAreaId) : "";
        if (!byArea.has(key)) byArea.set(key, []);
        byArea.get(key)!.push(r);
      }
      for (const rows of Array.from(byArea.values())) {
        await apiRequest("PUT", `/api/plan-age-rates/bulk/${newPlan.id}`, {
          ageBandType: rows[0].ageBandType || "five_year",
          ageBandRates: Object.fromEntries(rows.map((r: any) => [r.ageBand, r.monthlyRate])),
          ratingAreaId: rows[0].ratingAreaId ?? undefined,
          ratingAreaLabel: rows[0].ratingAreaLabel ?? undefined,
          effectiveStart: effectiveDateStr,
        });
      }

      // A multi-set (biannual/quarterly) year publishes through /rate-years;
      // the builder sends only the periods that carry rates, the rest can be
      // added later from the plan's Rates tab. Previously dropped here, so a
      // broker's cadence year never reached the server.
      if (data.rateYear) {
        await apiRequest("POST", `/api/plans/${newPlan.id}/rate-years`, data.rateYear);
      }

      // Generic benefit blocks (non-medical types; medical rides rawBenefits).
      const benefitRows: any[] = Array.isArray(data.benefits) ? data.benefits : [];
      if (benefitRows.length > 0) {
        await apiRequest("POST", "/api/plan-benefits/bulk", {
          planId: newPlan.id,
          benefits: Object.fromEntries(
            benefitRows.filter((b) => b.benefitType).map((b) => [b.benefitType, b]),
          ),
        });
      }

      // Auto-link the new library plan to the client/prospect/agency (M2M junction).
      if (hasClient) {
        await apiRequest("POST", "/api/client-plans", clientPlanLinkBody(parsedClientId, newPlan.id, effectiveDateStr, null));
      } else if (hasProspect) {
        await apiRequest("POST", `/api/prospects/${parsedProspectId}/plans`, { planId: newPlan.id });
      } else if (hasAgency) {
        await apiRequest("POST", "/api/agency-plans", { planId: newPlan.id });
      }
      return newPlan;
    },
    onSuccess: (newPlan: any) => {
      invalidateLinks();
      toast({
        title: "Plan created and linked",
        description: "Added to the plan library and linked here. Configure rates and benefits next.",
      });
      onOpenChange(false);
      if (hasClient) setLocation(`/plans/${newPlan.id}?clientId=${clientId}`);
      else if (hasProspect) setLocation(`/plans/${newPlan.id}?prospectId=${prospectId}`);
      else if (hasAgency) setLocation(`/plans/${newPlan.id}?agencyId=${agencyId}`);
      else setLocation(`/plans/${newPlan.id}`);
    },
    onError: (error: any) => {
      toast({ title: "Failed to create plan", description: String(error?.message || "Please try again."), variant: "destructive" });
    },
  });

  const targetLabel = hasAgency ? "agency" : hasProspect && !hasClient ? "prospect" : "client";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Plan</DialogTitle>
          <DialogDescription>
            {hasAgency
              ? "Plans your agency offers its own employees — pick an Administr library plan or create one manually."
              : !hasClient && !hasProspect
                ? "Every plan lives in the Plan Library and is offered to a specific client or prospect via their plan list."
                : `Every plan lives in the Plan Library and links to this ${targetLabel} via the client's plan list — pick an existing library plan or create one manually.`}
          </DialogDescription>
        </DialogHeader>

        {!hasClient && !hasProspect && !hasAgency ? (
          <div className="py-8 text-center text-sm text-muted-foreground space-y-4">
            <div>
              <Database className="mr-2 inline h-5 w-5" />
              Plans are added from a client's or prospect's Benefits tab, so
              they're linked to the right company.
            </div>
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  setLocation("/clients");
                }}
              >
                Go to Clients
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  setLocation("/prospects");
                }}
              >
                Go to Prospects
              </Button>
            </div>
            <p className="text-xs">
              Open a client or prospect, then Benefits &gt; Add Plan.
            </p>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as "library" | "manual")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="library" className="gap-2">
                <Database className="h-4 w-4" /> From Library
              </TabsTrigger>
              <TabsTrigger value="manual" className="gap-2">
                <Pencil className="h-4 w-4" /> Create New
              </TabsTrigger>
            </TabsList>

            <TabsContent value="library" className="space-y-4">
              <Alert>
                <Database className="h-4 w-4" />
                <AlertDescription>
                  {hasClient
                    ? "Links this client to the library plan — rates read from the Plan Library, locked per plan year. No copies are made, so library rate updates reach this client at its next renewal."
                    : hasAgency
                      ? "Adds the library plan to the plans your agency offers its own employees. No copies are made — rates read from the Plan Library."
                      : "References a library plan for quoting. A plan-year rate lock is created when the prospect converts to a client."}
                </AlertDescription>
              </Alert>
              {/* Guided flow: each step narrows the next. */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {hasClient && (
                  <div>
                    <label className="text-sm font-medium">1. Plan Year Effective Date</label>
                    <Input type="date" className="mt-1 h-10" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      Only library versions whose plan year contains this date are offered.
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium">{hasClient ? "2. " : "1. "}State</label>
                  <Input
                    className="mt-1 h-10 w-20 text-sm uppercase"
                    maxLength={2}
                    placeholder="CA"
                    value={manualAreaState || clientInfo?.state || ""}
                    onChange={(e) => setManualAreaState(e.target.value)}
                  />
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {clientInfo?.state
                      ? `From the ${targetLabel}'s primary location. Drives the rating-area step.`
                      : "No location on file — enter a state so rating areas resolve."}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">{hasClient ? "3. " : "2. "}Plan Type</label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="mt-1 h-10 text-sm">
                      <SelectValue placeholder="Select a plan type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {typeOptions.map((opt: any) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={filterType ? "" : "opacity-50 pointer-events-none"}>
                  <label className="text-sm font-medium">{hasClient ? "4. " : "3. "}Carrier</label>
                  <Select value={filterCarrierId} onValueChange={setFilterCarrierId} disabled={!filterType}>
                    <SelectTrigger className="mt-1 h-10 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any carrier</SelectItem>
                      {carriers
                        .filter((c) => carrierIdsForType?.has(c.id))
                        .map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isHealthType && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm font-medium">{hasClient ? "5. " : "4. "}Rating Area</label>
                    <Select value={filterAreaId} onValueChange={setFilterAreaId}>
                      <SelectTrigger className="mt-1 h-10 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All areas</SelectItem>
                        {uniqueStateAreas.map((a: any) => (
                          <SelectItem key={a.id} value={String(a.id)}>
                            {a.label || `Area ${a.areaNumber ?? a.id}`}
                            {String(a.id) === String(resolvedAreaNumber) ? " (client's area)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {resolvedAreaNumber != null ? (
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        Client rates at {resolvedArea?.label || `Area ${resolvedAreaNumber}`} (from its primary location).
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Metal Tier (optional)</label>
                    <Select value={filterTier} onValueChange={setFilterTier}>
                      <SelectTrigger className="mt-1 h-10 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All tiers</SelectItem>
                        {tiersForSelection.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Network (optional)</label>
                    <Select value={filterNetwork} onValueChange={setFilterNetwork}>
                      <SelectTrigger className="mt-1 h-10 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All networks</SelectItem>
                        {networkOptions.map((n) => (
                          <SelectItem key={n} value={n}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className={filterType ? "" : "opacity-50 pointer-events-none"}>
                <label className="text-sm font-medium">
                  {hasClient ? (isHealthType ? "6. " : "5. ") : isHealthType ? "5. " : "4. "}Plan
                </label>
                <div className="mt-1">
                  <SystemPlanCombobox
                    value={libraryPlan?.id ?? null}
                    onSelect={setLibraryPlan}
                    placeholder={filterType ? "Search matching plans..." : "Pick a plan type first"}
                    clientId={hasClient ? parsedClientId : null}
                    carrierIdFilter={filterCarrierId !== "all" ? parseInt(filterCarrierId, 10) : null}
                    typeFilter={filterType ? [filterType] : undefined}
                    metalTierFilter={filterTier !== "all" ? filterTier : null}
                    networkFilter={filterNetwork !== "all" ? filterNetwork : null}
                    ratingAreaIdFilter={isHealthType && filterAreaId !== "all" ? parseInt(filterAreaId, 10) : null}
                    effectiveDateContains={hasClient ? effectiveDate : null}
                  />
                </div>
              </div>
              {planYearMismatch && (
                <p className="text-sm text-red-600">{planYearMismatch}</p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button disabled={!libraryPlan || !!planYearMismatch || linkMutation.isPending} onClick={() => linkMutation.mutate()}>
                  {linkMutation.isPending ? "Adding..." : hasClient ? "Link Plan to Client" : hasAgency ? "Add Plan to Agency" : "Add Plan to Prospect"}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="manual" className="space-y-4">
              <Alert>
                <Pencil className="h-4 w-4" />
                <AlertDescription>
                  The same Plan Builder the platform library uses. Creates the plan in your
                  agency's Plan Library and links it to this {targetLabel}.
                </AlertDescription>
              </Alert>
              <PlanBuilderForm
                variant="broker"
                carriers={carriers.map((c) => ({ isActive: true, ...c })) as any}
                onSave={(data) => createMutation.mutate(data)}
                isPending={createMutation.isPending}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
